/**
 * projeBeyaniUyariService.js — Proje Beyanı Uyarıları Servis Dosyası
 *
 * Ekran: /Management/Fairs/Rules?tab=uyari (pages/Management/Fairs/ProjeBeyaniUyarilariTab.jsx)
 * Base URL (future): https://gw.sakagrup.com:8443/api/v1/projects/declaration-warnings
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 *
 * Dekoratörün "Proje Beyanı" formunu doldururken (bkz. CreateProjectModal,
 * FirmaProjelerListPage.jsx) Sınıflandırma/Stand Türü Seçimi bölümü DIŞINDAKİ
 * her alanın altına gösterilecek serbest metin bir uyarı tutar — örn. "Ahşap
 * standlar bu fuarda 24m²'den küçük olamaz." Sınıflandırma (Stand Türü/Proje
 * Türü/Proje Yapısı/Proje Tipi) kasıtlı olarak DECLARATION_FIELDS katalogunda
 * YOK — admin tarafından zaten atanan/seçilen alanlar, uyarı gerektirmiyor.
 *
 * Uyarılar fuar bazlıdır (selectedFuarId) — her fuar için ayrı bir set.
 *
 * 2026-08-11: getDeclarationWarnings artık forceLive destekliyor — dekoratör
 * tarafındaki gerçek Proje Beyanı modalı (CreateProjectModal, FirmaProjelerListPage.jsx)
 * bunu fuarId ile açılışta çağırıp active:true satırları ilgili alanın altında gösteriyor.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/projects/declaration-warnings`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

// Sabit alan kataloğu — CreateProjectModal'daki (FirmaProjelerListPage.jsx)
// "Proje Beyanı" modalının Sınıflandırma dışındaki bölümleri: Ölçüler (H Max/
// Proje Alanı/Kroki), Teknik Talepler (tek grup uyarı — Elektrik/Su/Hava/
// İnternet ayrı ayrı değil), Sürdürülebilirlik Beyanı.
export const DECLARATION_FIELDS = [
  { key: 'hMax', get label() { return i18n.t("services:projeBeyaniUyariService.hMaxMaksimumDuvarYuksekligi"); }, get section() { return i18n.t("services:projeBeyaniUyariService.olculer"); } },
  { key: 'm2', get label() { return i18n.t("services:projeBeyaniUyariService.projeAlaniM"); }, get section() { return i18n.t("services:projeBeyaniUyariService.olculer"); } },
  { key: 'kroki', get label() { return i18n.t("services:projeBeyaniUyariService.krokiDuvarOlculeri"); }, get section() { return i18n.t("services:projeBeyaniUyariService.olculer"); } },
  { key: 'teknikTalepler', get label() { return i18n.t("services:projeBeyaniUyariService.teknikTaleplerElektrikSuHava"); }, get section() { return i18n.t("services:projeBeyaniUyariService.teknikTalepler"); } },
  { key: 'surdurulebilirlik', get label() { return i18n.t("services:projeBeyaniUyariService.surdurulebilirlikBeyani"); }, get section() { return i18n.t("services:projeBeyaniUyariService.surdurulebilirlik"); } },
];

// fuarId -> { [fieldKey]: { text, active, updatedAt } } — demo fuarId 3, diğer
// projelerdeki mock veri setleriyle aynı ("standart" mock fuar, bkz. projectService.js).
const _mockWarningsByFuar = {
  3: {
    m2: {
      text: "Ahşap standlar bu fuarda 24m²'den küçük olamaz.",
      active: true,
      updatedAt: '2026-07-30T09:00:00Z',
    },
  },
};

function buildRows(fuarId) {
  const stored = _mockWarningsByFuar[fuarId] || {};
  return DECLARATION_FIELDS.map((f) => {
    const saved = stored[f.key];
    return {
      fuarId,
      fieldKey: f.key,
      fieldLabel: f.label,
      section: f.section,
      text: saved?.text || '',
      active: saved?.active || false,
      updatedAt: saved?.updatedAt || null,
    };
  });
}

// GET — seçili fuar için 5 satırlık sabit alan listesi (kayıtlı uyarı yoksa boş metin).
export async function getDeclarationWarnings(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return buildRows(fuarId);
  }
  try {
    const res = await axios.get(BASE, { params: { fuarId }, headers: getAuthHeader() });
    return res.data;
  } catch (e) {
    console.error('getDeclarationWarnings başarısız:', e);
    throw e;
  }
}

// PUT — bir alanın uyarı metnini/aktiflik durumunu oluştur veya güncelle.
export async function setDeclarationWarning(fuarId, fieldKey, { text, active }) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    if (!_mockWarningsByFuar[fuarId]) _mockWarningsByFuar[fuarId] = {};
    _mockWarningsByFuar[fuarId][fieldKey] = { text, active, updatedAt: new Date().toISOString() };
    return buildRows(fuarId).find((r) => r.fieldKey === fieldKey);
  }
  try {
    const res = await axios.put(
      `${BASE}/${fieldKey}`,
      { fuarId, text, active },
      { headers: getAuthHeader() }
    );
    return res.data;
  } catch (e) {
    console.error('setDeclarationWarning başarısız:', e);
    throw e;
  }
}

// Uyarıyı kaldır — metni boşaltıp pasif hale getirir (satır kataloğu sabit
// olduğundan gerçek bir "silme" yok, bkz. dosya başı yorumu).
export async function clearDeclarationWarning(fuarId, fieldKey) {
  return setDeclarationWarning(fuarId, fieldKey, { text: '', active: false });
}
